import type { NoteEditOrigin } from "@rosc/contracts";
import {
  ChevronDown,
  FilePlus2,
  FileText,
  Loader2,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import type { JSX, ReactNode } from "react";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  DiffLine,
  FactChange,
  NoteAssistantTarget,
  ProposeNoteEditResponse,
} from "@/common/types/noteAssistant";
import { AssistantActions } from "@/components/ui/AssistantActions";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DictationTextarea } from "@/components/ui/DictationTextarea";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/hooks/use-toast";
import { useNoteAssistant } from "@/hooks/visits/useNoteAssistant";
import { changeText } from "@/lib/assistantChangeStyles";
import { cn } from "@/lib/utils";

// A prepared instruction the panel fills in and proposes at once, e.g. the
// note update for a transcript correction.
export type AssistantRequest = {
  key: number;
  instruction: string;
  origin: NoteEditOrigin;
  // Corrections in the instruction, and those that did not fit.
  changes: number;
  omitted: number;
};

type Props = {
  target: NoteAssistantTarget;
  onApplied: () => void;
  onClose: () => void;
  // Inside the workspace assistant panel, which has its own title and close
  // control: drop this panel's header and card frame.
  embedded?: boolean;
  // Selected visit sources the proposal may draw on.
  sourceIds?: readonly number[];
  // In the instruction box's bottom-left corner (the attach-file button).
  composerAccessory?: ReactNode;
  // Under the instruction box (attached-file chips).
  composerFooter?: ReactNode;
  // Told when a proposal appears or goes, so the host can make room.
  onProposalShownChange?: (shown: boolean) => void;
  request?: AssistantRequest | null;
  // Called once the request was taken, so it is not proposed again.
  onRequestTaken?: () => void;
  // Files still attaching (fast attach): the instruction waits for them.
  waitingForFiles?: boolean;
};

const NO_SOURCES: readonly number[] = [];
const INSTRUCTION_ID = "note-assistant-instruction";

// "Add sources to note" fills in the instruction, which can sit below the
// fold of the assistant panel: bring it into view and put the cursor at the
// end, so the clinician sees the wording, can adjust it and then proposes.
const revealInstruction = (): void => {
  requestAnimationFrame(() => {
    const field = document.getElementById(INSTRUCTION_ID);
    if (!(field instanceof HTMLTextAreaElement)) return;
    const reduced = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    field.scrollIntoView?.({
      behavior: reduced ? "auto" : "smooth",
      block: "center",
    });
    field.focus({ preventScroll: true });
    field.setSelectionRange(field.value.length, field.value.length);
  });
};

const STATUS_STYLE: Record<FactChange["status"], string> = {
  kept: changeText.neutral,
  "changed-by-instruction": changeText.changed,
  LOST: changeText.danger,
  ADDED: changeText.danger,
};

// The kind is shown next to the value, so "allergy: x" reads as just "x".
const displayFact = (label: string | null): string =>
  (label ?? "").replace(/^allergy: /, "");

// Keeps one unchanged line around each change and folds the rest, so the
// changed lines are visible without scrolling.
type DiffRow = DiffLine | { op: "fold"; count: number };
const CONTEXT = 1;
const foldUnchanged = (diff: DiffLine[]): DiffRow[] => {
  const near = (index: number): boolean =>
    diff
      .slice(Math.max(0, index - CONTEXT), index + CONTEXT + 1)
      .some((line) => line.op !== "equal");
  const rows: DiffRow[] = [];
  diff.forEach((line, index) => {
    if (line.op !== "equal" || near(index)) {
      rows.push(line);
      return;
    }
    const last = rows[rows.length - 1];
    if (last?.op === "fold") last.count++;
    else rows.push({ op: "fold", count: 1 });
  });
  return rows;
};

const Collapsible = ({
  title,
  defaultOpen,
  children,
}: {
  title: React.ReactNode;
  defaultOpen?: boolean;
  children: React.ReactNode;
}): JSX.Element => (
  <details className="group" open={defaultOpen}>
    <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground sm:min-h-8 [&::-webkit-details-marker]:hidden">
      <span>{title}</span>
      <ChevronDown className="h-4 w-4 shrink-0 transition-transform group-open:rotate-180" />
    </summary>
    <div className="pt-2">{children}</div>
  </details>
);

// Edit a generated note by typed or spoken instruction. The provider sees the
// line diff and a deterministic clinical fact check (lost or added facts in
// red) and must confirm before anything is saved.
export const NoteAssistantPanel = ({
  target,
  onApplied,
  onClose,
  embedded = false,
  sourceIds = NO_SOURCES,
  composerAccessory,
  composerFooter,
  onProposalShownChange,
  request,
  onRequestTaken,
  waitingForFiles = false,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [instruction, setInstruction] = useState("");
  const [isDictating, setIsDictating] = useState(false);
  // Set by a request; cleared when the clinician empties the instruction.
  const [origin, setOrigin] = useState<{
    origin: NoteEditOrigin;
    changes: number;
    omitted: number;
  } | null>(null);
  const [autoPropose, setAutoPropose] = useState<string | null>(null);
  const assistant = useNoteAssistant(
    target,
    instruction,
    sourceIds,
    origin?.origin,
  );
  const { result, proposing, proposeFailed, applying } = assistant;
  const [acknowledgedProposal, setAcknowledgedProposal] =
    useState<ProposeNoteEditResponse | null>(null);
  const acknowledged = !!result && acknowledgedProposal === result;
  const actionsRef = useRef<HTMLDivElement>(null);
  // While a proposal is shown the instruction box gets shorter, so the
  // proposal and Apply fit in the panel without scrolling.
  const compact = !!result && !proposing;
  useEffect(() => {
    onProposalShownChange?.(compact);
    return () => onProposalShownChange?.(false);
  }, [compact, onProposalShownChange]);

  // A new proposal: bring Apply into view if the panel is short, without
  // moving anything that already fits.
  useEffect(() => {
    if (!result || proposing) return;
    requestAnimationFrame(() => {
      actionsRef.current?.scrollIntoView?.({ block: "nearest" });
    });
  }, [result, proposing]);

  const handlePropose = async (): Promise<void> => {
    if (waitingForFiles) return;
    setAcknowledgedProposal(null);
    await assistant.propose();
  };
  // Only a new request matters here.
  // biome-ignore lint/correctness/useExhaustiveDependencies: keyed by request
  useEffect(() => {
    if (!request) return;
    onRequestTaken?.();
    assistant.clear();
    setOrigin({
      origin: request.origin,
      changes: request.changes,
      omitted: request.omitted,
    });
    setInstruction(request.instruction);
    setAutoPropose(request.instruction);
  }, [request?.key]);
  // Proposes once the requested instruction is in place.
  // biome-ignore lint/correctness/useExhaustiveDependencies: fires per request
  useEffect(() => {
    if (autoPropose === null || instruction !== autoPropose) return;
    setAutoPropose(null);
    void handlePropose();
  }, [autoPropose, instruction]);

  const handleApply = async (): Promise<void> => {
    const outcome = await assistant.apply(acknowledged);
    if (!outcome || !assistant.isCurrent()) return;
    if (outcome === "saved") {
      setInstruction("");
      setOrigin(null);
      toast({ variant: "success", title: t("noteAssistant.appliedToast") });
      onApplied();
    } else if (outcome === "stale") {
      toast({ variant: "error", title: t("noteAssistant.staleError") });
    } else if (outcome === "uncertain") {
      toast({ variant: "error", title: t("noteAssistant.applyUncertain") });
    } else if (outcome === "error") {
      toast({ variant: "error", title: t("noteAssistant.applyError") });
    }
  };

  const unchanged = result ? result.proposed === result.current : false;

  return (
    <section
      aria-label={t("noteAssistant.title")}
      className={cn(
        "min-w-0",
        embedded
          ? "space-y-3"
          : "space-y-4 rounded-lg border bg-muted/30 p-3 sm:p-4",
      )}
      data-testid="note-assistant"
    >
      <div
        className={cn(
          "flex items-center justify-between gap-2",
          embedded && "hidden",
        )}
      >
        <h3 className="flex items-center gap-2 font-semibold">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
            <Sparkles className="h-4 w-4 text-primary" />
          </span>
          {t("noteAssistant.title")}
        </h3>
        <Button
          aria-label={t("common.close")}
          className="h-11 w-11 sm:h-8 sm:w-8"
          size="icon"
          type="button"
          variant="ghost"
          onClick={onClose}
        >
          <X />
        </Button>
      </div>

      {sourceIds.length > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-x-2 rounded-lg border bg-background py-0.5 pl-3 pr-1 text-sm"
          data-testid="assistant-source-use"
        >
          <span className="text-muted-foreground">
            {t("noteAssistant.usingSources", { count: sourceIds.length })}
          </span>
          <Button
            className="h-11 px-2 text-primary sm:h-8"
            disabled={proposing || applying || isDictating}
            type="button"
            variant="ghost"
            onClick={() => {
              setInstruction(t("noteAssistant.incorporateInstruction"));
              setOrigin(null);
              revealInstruction();
            }}
          >
            <FilePlus2 />
            {t("noteAssistant.incorporateButton")}
          </Button>
        </div>
      )}

      {origin?.origin === "transcript-edit" && (
        <div
          className="space-y-1 rounded-lg border bg-background px-3 py-2 text-sm"
          data-testid="assistant-transcript-origin"
        >
          <p className="flex items-center gap-2 font-medium">
            <FileText aria-hidden className="size-4 shrink-0 text-primary" />
            {t("noteAssistant.fromTranscript", { count: origin.changes })}
          </p>
          <p className="text-xs text-muted-foreground">
            {t("noteAssistant.fromTranscriptHint")}
          </p>
          {origin.omitted > 0 && (
            <p className={cn("text-xs", changeText.dangerText)}>
              {t("noteAssistant.omittedCorrections", {
                count: origin.omitted,
              })}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label className={cn(compact && "sr-only")} htmlFor={INSTRUCTION_ID}>
          {t("noteAssistant.instructionLabel")}
        </Label>
        <DictationTextarea
          id={INSTRUCTION_ID}
          placeholder={t("noteAssistant.instructionPlaceholder")}
          value={instruction}
          onChange={(value) => {
            setInstruction(value);
            if (!value.trim()) setOrigin(null);
          }}
          onDictatingChange={setIsDictating}
          accessory={composerAccessory}
          attachments={composerFooter}
          // A transcript correction's instruction runs over several lines:
          // keep it readable next to the proposal.
          compact={compact && !origin}
          action={
            <Button
              className="h-11 w-full sm:h-9 sm:w-auto"
              disabled={
                !instruction.trim() ||
                proposing ||
                applying ||
                isDictating ||
                waitingForFiles
              }
              title={
                waitingForFiles
                  ? t("visit.workspace.sources.waitForFiles")
                  : undefined
              }
              type="button"
              onClick={() => void handlePropose()}
            >
              {proposing && <Loader2 className="animate-spin" />}
              {t(
                proposing
                  ? "noteAssistant.proposing"
                  : "noteAssistant.proposeButton",
              )}
            </Button>
          }
        />
      </div>

      {proposeFailed && !proposing && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
          role="alert"
        >
          {t("noteAssistant.proposeError")}
          <Button
            className="h-11 sm:h-8"
            size="sm"
            type="button"
            variant="outline"
            onClick={() => void handlePropose()}
          >
            {t("noteAssistant.retry")}
          </Button>
        </div>
      )}

      {proposing && (
        <div
          aria-hidden
          className="space-y-2 rounded-lg border bg-background p-3"
        >
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-5/6" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      )}

      {result && !proposing && (
        <div className="min-w-0 space-y-3" data-testid="note-proposal">
          <ProposalReview
            acknowledged={acknowledged}
            fromTranscript={origin?.origin === "transcript-edit"}
            result={result}
            onAcknowledgedChange={(value) => {
              setAcknowledgedProposal(value ? result : null);
            }}
          />

          <AssistantActions ref={actionsRef} className={cn(embedded && "mb-0")}>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                assistant.clear();
              }}
            >
              {t("noteAssistant.discardButton")}
            </Button>
            <Button
              type="button"
              disabled={
                applying || unchanged || (result.blocked && !acknowledged)
              }
              onClick={() => void handleApply()}
            >
              {applying && <Loader2 className="animate-spin" />}
              {t("noteAssistant.applyButton")}
            </Button>
          </AssistantActions>
        </div>
      )}
    </section>
  );
};

// The proposal as the clinician checks it: inserted shortcuts, the clinical
// fact check (lost or added facts in red), the line diff and, when facts
// changed, the acknowledgement Apply needs. Shared by the classic panel and
// the v3.2 assistant conversation.
export const ProposalReview = ({
  result,
  acknowledged,
  onAcknowledgedChange,
  fromTranscript = false,
  preChart = false,
}: {
  result: ProposeNoteEditResponse;
  // A pre-chart draft: what the clinician added is expected, not a risk.
  preChart?: boolean;
  acknowledged: boolean;
  // A proposal for a transcript correction: every changed fact needs a check.
  fromTranscript?: boolean;
  onAcknowledgedChange: (acknowledged: boolean) => void;
}): JSX.Element => {
  const { t } = useTranslation();
  const changes = result.factChanges ?? [];
  const flagged = changes.filter((c) => c.status !== "kept");
  const kept = changes.filter((c) => c.status === "kept");
  const count = (status: FactChange["status"]): number =>
    changes.filter((c) => c.status === status).length;
  const unchanged = result.proposed === result.current;
  const style = (status: FactChange["status"]): string =>
    preChart && status === "ADDED" ? changeText.changed : STATUS_STYLE[status];
  const label = (status: FactChange["status"]): string =>
    preChart && status === "ADDED"
      ? t("preChart.factAdded")
      : t(`noteAssistant.status.${status}`);
  const changedLines = result.diff.filter((line) => line.op !== "equal").length;
  // Long diffs start folded on phones so the fact check and actions stay close.
  const showDiffOpen =
    changedLines <= 8 || window.matchMedia("(min-width: 640px)").matches;
  return (
    <>
      {(result.macros.inserted.length > 0 ||
        result.macros.skipped.length > 0) && (
        <div className="space-y-2 rounded-lg border bg-background p-3 text-sm">
          {result.macros.inserted.map((m) => (
            <p key={`in-${m.trigger}`} className="wrap-break-word">
              {t("noteAssistant.macroInserted", {
                trigger: m.trigger,
                section: m.section,
              })}{" "}
              <span className="font-medium">{m.text}</span>
            </p>
          ))}
          {result.macros.skipped.map((m) => (
            <p
              key={`sk-${m.trigger}`}
              className="wrap-break-word text-muted-foreground"
            >
              {t(`noteAssistant.macroSkipped.${m.reason}`, {
                spoken: m.spoken,
              })}
            </p>
          ))}
          {result.macros.notApplied && (
            <p className="wrap-break-word text-muted-foreground">
              {t("noteAssistant.notApplied", {
                text: result.macros.notApplied,
              })}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2" data-testid="fact-check">
        <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          {result.blocked ? (
            <ShieldAlert className="h-4 w-4 text-destructive" />
          ) : (
            <ShieldCheck className="h-4 w-4 text-success" />
          )}
          {t("noteAssistant.factCheck")}
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          {(["kept", "changed-by-instruction", "LOST", "ADDED"] as const).map(
            (status) =>
              (status === "kept" || count(status) > 0) && (
                <span
                  key={status}
                  className={cn(
                    "rounded-full px-2.5 py-1 font-medium",
                    style(status),
                  )}
                >
                  {count(status)} {label(status)}
                </span>
              ),
          )}
        </div>

        {flagged.length > 0 && (
          <ul className="space-y-2">
            {flagged.map((change) => (
              <li
                key={`${change.status}-${change.before}-${change.after}`}
                className={cn(
                  "rounded-md border bg-background p-2.5 text-sm",
                  (change.status === "LOST" ||
                    (change.status === "ADDED" && !preChart)) &&
                    "border-destructive/40",
                )}
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span
                    className={cn(
                      "rounded-full px-2 py-0.5 text-xs font-semibold",
                      style(change.status),
                    )}
                  >
                    {label(change.status)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {t(`noteAssistant.kind.${change.kind}`, {
                      defaultValue: change.kind,
                    })}
                  </span>
                  {change.inTranscript && (
                    <span className="text-xs text-muted-foreground">
                      · {t("noteAssistant.inTranscript")}
                    </span>
                  )}
                  {change.inSource && (
                    <span className="text-xs text-muted-foreground">
                      · {t("noteAssistant.inSource")}
                    </span>
                  )}
                </div>
                <p className="mt-1 wrap-break-word">
                  {change.before && (
                    <span className={cn(change.after && changeText.before)}>
                      {displayFact(change.before)}
                    </span>
                  )}
                  {change.before && change.after && " → "}
                  {change.after && (
                    <span className="font-medium">
                      {displayFact(change.after)}
                    </span>
                  )}
                  {!change.after && (
                    <span className={changeText.dangerText}>
                      {" "}
                      · {t("noteAssistant.missing")}
                    </span>
                  )}
                </p>
              </li>
            ))}
          </ul>
        )}

        {kept.length > 0 && (
          <Collapsible
            title={t("noteAssistant.keptFacts", { count: kept.length })}
          >
            <ul className="flex flex-wrap gap-1.5">
              {kept.map((c) => (
                <li
                  key={c.before}
                  className={cn(
                    "rounded-full px-2.5 py-1 text-xs",
                    changeText.neutral,
                  )}
                >
                  {displayFact(c.before)}
                </li>
              ))}
            </ul>
          </Collapsible>
        )}
      </div>

      <Collapsible
        defaultOpen={showDiffOpen}
        title={
          <>
            {t("noteAssistant.changesTitle")}
            {!unchanged && (
              <span className="ml-1 font-normal normal-case">
                · {t("noteAssistant.changedLines", { count: changedLines })}
              </span>
            )}
          </>
        }
      >
        {unchanged ? (
          <p className="text-sm text-muted-foreground">
            {t("noteAssistant.noChanges")}
          </p>
        ) : (
          <div className="max-h-[50vh] overflow-y-auto overflow-x-hidden rounded-md border bg-background py-1 text-sm leading-relaxed sm:max-h-80">
            {foldUnchanged(result.diff).map((row, index) =>
              row.op === "fold" ? (
                <div
                  // Rows can repeat; the position is their identity here.
                  key={index}
                  className={cn(
                    "px-3 py-0.5 text-xs italic",
                    changeText.unchangedLine,
                  )}
                >
                  {t("noteAssistant.unchangedLines", { count: row.count })}
                </div>
              ) : (
                <div
                  key={index}
                  className={cn(
                    "whitespace-pre-wrap wrap-break-word border-l-[3px] px-3 py-0.5 wrap-anywhere",
                    row.op === "insert" && changeText.addedLine,
                    row.op === "delete" && changeText.removedLine,
                    row.op === "equal" && changeText.unchangedLine,
                  )}
                >
                  <span className="sr-only">
                    {row.op === "insert" && t("noteAssistant.addedLine")}
                    {row.op === "delete" && t("noteAssistant.removedLine")}
                  </span>
                  {row.text || " "}
                </div>
              ),
            )}
          </div>
        )}
      </Collapsible>

      {result.blocked && (
        <div
          className="space-y-1 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-sm"
          role="alert"
        >
          <p className={cn("font-medium", changeText.dangerText)}>
            {t(
              fromTranscript
                ? "noteAssistant.transcriptBlockedWarning"
                : "noteAssistant.blockedWarning",
            )}
          </p>
          <Label
            className="flex min-h-11 cursor-pointer items-center gap-3 font-normal leading-snug"
            htmlFor="note-assistant-acknowledge"
          >
            <Checkbox
              checked={acknowledged}
              className="h-5 w-5"
              id="note-assistant-acknowledge"
              onCheckedChange={(value) => {
                onAcknowledgedChange(value === true);
              }}
            />
            {t("noteAssistant.acknowledge")}
          </Label>
        </div>
      )}
    </>
  );
};
